import { BorrowerType, TrusteeType } from '@/helpers/const';
import { toApplicantAssets } from './application-progress';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import type { FundsAssetFields } from './application-progress';
import type { EntityIncome, LoanApplication } from '@/types';
import type { ScenarioBorrowerPrefill } from './borrower';

type BorrowerRef = { borrowerId?: string | null; applicantId?: string | null };
type PropertyRef = { id?: string };
type ScenarioOwnershipRow = BorrowerRef & {
  incomingPropertyId?: string | null;
  outgoingPropertyId?: string | null;
  supportingPropertyId?: string | null;
  share?: number | null;
};

export type ScenarioBorrowerFinancialsSource = {
  applicants: { id?: string }[];
  fundsAssets?: (FundsAssetFields & BorrowerRef)[];
  fundsAdditionalIncome?: (BorrowerRef & { incomeType?: string; amount?: number | null })[];
  propertiesOwnership?: ScenarioOwnershipRow[];
  incomingProperties?: PropertyRef[];
  outgoingProperties?: PropertyRef[];
  supportingProperties?: PropertyRef[];
};

const PROPERTY_KEYS = {
  incoming: ['incoming-property', 'incomingProperties', 'incomingPropertyId'],
  outgoing: ['outgoing-property', 'outgoingProperties', 'outgoingPropertyId'],
  supporting: ['supporting-property', 'supportingProperties', 'supportingPropertyId'],
} as const;

/** Companies and corporate trusts are the borrowers the application keeps entity assets and net profit for. */
const holdsEntityFinancials = (group: BorrowerGroup) =>
  group.type === BorrowerType.COMPANY ||
  (group.type === BorrowerType.TRUST && group.trustDetails?.trusteeType === TrusteeType.COMPANY);

/**
 * Carries a scenario's borrower-linked assets, net profit and property shares into the pre-filled application.
 * Assets and net profit go to company and corporate trust borrowers. Property shares are keyed by borrower group:
 * a borrower's share goes to its group and an applicant's share to their individual group, so a director's personal
 * share is never moved onto their company. A property no scenario row resolves for keeps its existing share.
 */
export const applyScenarioBorrowerFinancials = (
  loanApp: LoanApplication,
  scenario: ScenarioBorrowerFinancialsSource,
  { groups, groupIdByBorrowerId }: ScenarioBorrowerPrefill,
): void => {
  const entityGroupIds = new Set(groups.filter(holdsEntityFinancials).map((group) => group.id));
  const entityGroupIdFor = (borrowerId?: string | null) => {
    const groupId = borrowerId ? groupIdByBorrowerId.get(borrowerId) : undefined;
    return groupId && entityGroupIds.has(groupId) ? groupId : undefined;
  };

  const entityAssets: Record<string, FundsAssetFields[]> = {};
  for (const asset of scenario.fundsAssets ?? []) {
    const groupId = entityGroupIdFor(asset.borrowerId);
    if (groupId && asset.assetType) (entityAssets[groupId] ??= []).push(asset);
  }
  if (Object.keys(entityAssets).length) {
    loanApp.entityAssets = Object.fromEntries(
      Object.entries(entityAssets).map(([groupId, assets]) => [groupId, toApplicantAssets(assets)]),
    );
  }

  const entityIncome: EntityIncome = {};
  for (const income of scenario.fundsAdditionalIncome ?? []) {
    const groupId = income.incomeType === 'net_profit' ? entityGroupIdFor(income.borrowerId) : undefined;
    if (!groupId) continue;
    // Scenario net profit is captured as an annual figure; a borrower's rows are summed.
    const amount = (entityIncome[groupId]?.netProfit.amount ?? 0) + (income.amount ?? 0);
    entityIncome[groupId] = { netProfit: { amount, frequency: 'year' }, borrowerGroupId: groupId };
  }
  if (Object.keys(entityIncome).length) loanApp.entityIncome = entityIncome;

  const individualGroupIdByApplicantId = new Map(
    groups.flatMap((group) =>
      group.type === BorrowerType.INDIVIDUAL && group.applicants?.[0]
        ? [[group.applicants[0].applicantId, group.id] as const]
        : [],
    ),
  );
  const formApplicantIdByApplicantId = new Map(
    scenario.applicants.flatMap((applicant, index) =>
      applicant.id ? [[applicant.id, `applicant-${index}`] as const] : [],
    ),
  );
  const ownerGroupId = (row: ScenarioOwnershipRow) => {
    if (row.borrowerId) return groupIdByBorrowerId.get(row.borrowerId);
    const formApplicantId = row.applicantId ? formApplicantIdByApplicantId.get(row.applicantId) : undefined;
    return formApplicantId ? individualGroupIdByApplicantId.get(formApplicantId) : undefined;
  };

  loanApp.propertiesOwnership = loanApp.propertiesOwnership.map((ownership) => {
    const keys = PROPERTY_KEYS[ownership.type as keyof typeof PROPERTY_KEYS];
    if (!keys) return ownership;
    const [idPrefix, propertiesKey, ownershipKey] = keys;
    const propertyIndex = Number(String(ownership.id).replace(`${idPrefix}-`, ''));
    const propertyId = scenario[propertiesKey]?.[propertyIndex]?.id;
    if (!propertyId) return ownership;

    const share: Record<string, number> = {};
    for (const row of scenario.propertiesOwnership ?? []) {
      const groupId = row[ownershipKey] === propertyId ? ownerGroupId(row) : undefined;
      if (groupId && row.share) share[groupId] = (share[groupId] ?? 0) + row.share;
    }
    return Object.keys(share).length ? { ...ownership, share } : ownership;
  });
};
